import React, { useEffect, useState, Suspense } from 'react';
import styled from 'styled-components';
import {
  closeAssignment,
  getAssignments,
  getClasses,
  getDocumentReviews,
  getSubmissionsByAssignmentId,
  startSubmission,
  getAssignmentById,
  getClassesWithMembers,
} from '../../../service';
import dayjs from 'dayjs';
import ReactiveRender, {
  isMobileView,
  isTabletView,
} from '../../ReactiveRender';
import TeacherTasksDesktop from '../TeacherTasksDesktop';
import { taskHeaderProps } from '../../../utils/headerProps.js';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import Loader from '../../Loader';
import { TaskListSkeleton } from '../../Skeleton';
import DeleteAssignmentPopup from '../../DeleteAssignmentPopUp';
import DatePickerPopup from '../../DatePickerPopUp/index.jsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalenderContainer,
  FilterAndSortContainer,
  MainContainer,
  TasksImg,
  TasksImgCal,
} from '../../StudentTaskRoot/style.js';

import { FilterContainer, FilterLine } from '../../CompletedPage/style.js';
import RoundedDropDown from '../../../components2/RoundedDropDown/index.jsx';
import TaskSelected from '../../../static/img/Columns-new.svg';
import TaskNotSelected from '../../../static/img/Columns-new-gray.svg';
import FilterSquare from '../../../static/img/filter-square.svg';

import CalSelected from '../../../static/img/Calendar-new.svg';
import CalNotSelected from '../../../static/img/Calendar-new-purple.svg';

const MyCalendar = React.lazy(() => import('../../../components2/Calender/index.js'));
import { TitleHeading } from '../../FilterSort/style.js';
import MenuButton from '../../MenuButton/index.jsx';
import { useClassData } from '../../state/hooks.js';
import { duplicateAssignment } from '../../../utils/function.js';
import SortItems from '../../../components2/SortItems/index.jsx';
import GeneralPopup from '../../GeneralPopup/index.jsx';
import Toast from '../../Toast';
import { toast } from 'react-toastify';
import {
  getAwaitingSubmissions,
  getDraftTasks,
  getInProgressTasks,
} from '../../../service/tasks.js';
import {
  extendDueAtAssignment,
  schedulepublishAssignment,
} from '../../../service';
import {
  isDueDateNotSet,
  isDueDateOneHourAfterPublish,
} from '../../CreateAAssignmentLaptop/rules';
import RubricOutcomesPopup from '../../RubricOutcomesPopup/index.jsx';
import { getUserId } from '../../../userLocalDetails';
import { BulkUploadDialog } from '../../BulkUpload/components/BulkUploadDialog';
import { BulkUploadFullPage } from '../../BulkUpload/components/BulkUploadFullPage';
import { useBulkUploadGate } from '../../BulkUpload/components/BulkUploadPlanGate';
import {
  excludeStudentsWithSubmissions,
  getStudentsForAssignment,
} from '../../BulkUpload/utils/students';
import { openedSubmissionPath } from '../../BulkUpload/utils/feedbackRun';
import { invalidateBulkUploadQueries } from '../../../hooks/bulkUploadQueries';

export default function TeacherTaskRoot() {
  const [assignments, setAssignments] = React.useState([]);
  const [filteredTasks, setFilteredTasks] = React.useState([]);
  const [classes, setClasses] = React.useState([]);
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);
  const [selectedAssignment, setSelectedAssignment] = React.useState(null);
  const [showDateExtendPopup, setShowDateExtendPopup] = React.useState(false);
  const { data: classData, isLoadingdata: isLoadingclassData } = useClassData();
  const [sortItem, setSortItem] = React.useState(true);
  const [selectedClass, setSelectedClass] = React.useState('');
  const [tasksSelected, setTasksSelected] = React.useState(true);
  const [isShowMenu, setShowMenu] = React.useState(false);
  const [closeTask, setCloseTask] = useState({ popup: false, id: null });
  const [showSchedulePublishPopup, setShowSchedulePublishPopup] =
    React.useState(false);
  const [showAssignmentStatusPopup, setShowAssignmentStatusPopup] = useState(false);
  const [selectedAssignmentForStatus, setSelectedAssignmentForStatus] = useState(null);
  const [bulkUploadTaskId, setBulkUploadTaskId] = useState(null);
  const bulkUploadGate = useBulkUploadGate();
  const [taskStudents, setTaskStudents] = useState([]);
  const mobileView = isMobileView();
  const tabletView = isTabletView();
  const queryClient = useQueryClient();

  const assignmentsQuery = useQuery({
    queryKey: ['assignments'],
    queryFn: () => getAssignments(),
  });
  const documentReviewTasksQuery = useQuery({
    queryKey: ['document-reviews'],
    queryFn: async () => {
      return await getDocumentReviews();
    },
  });
  const teacherClassesQuery = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const result = await getClasses();
      return result;
    },
    staleTime: 300_000,
  });

  React.useEffect(() => {
    if (assignmentsQuery.data) {
      const withType = assignmentsQuery.data.map((a) => ({ ...a, type: 'TASK' }));
      const merged = documentReviewTasksQuery.data
        ? [...withType, ...documentReviewTasksQuery.data]
        : withType;
      setAssignments(merged);
    }
    if (teacherClassesQuery.data) setClasses(teacherClassesQuery.data);
  }, [assignmentsQuery.data, teacherClassesQuery.data, documentReviewTasksQuery.data]);

  

  const assignmentsFilteredByClass = React.useMemo(
    () => {
      if (!selectedClass) return assignments;
      return assignments.filter((assignment) => {
        const classIds = assignment.classIds || (assignment.classId ? [assignment.classId] : []);
        return classIds.includes(selectedClass);
      });
    },
    [assignments, selectedClass]
  );

  React.useEffect(() => {
    setFilteredTasks(assignmentsFilteredByClass);
  }, [assignmentsFilteredByClass]);

  // Fetch students for bulk upload when task is selected
  React.useEffect(() => {
    if (!bulkUploadTaskId) {
      setTaskStudents([]);
      return;
    }

    let cancelled = false;

    Promise.all([
      getAssignmentById(bulkUploadTaskId).catch(() => null),
      getClassesWithMembers().catch(() => ({ classes: [] })),
      getSubmissionsByAssignmentId(bulkUploadTaskId).catch(() => []),
    ]).then(([assignment, classBundle, submissions]) => {
      if (cancelled) return;
      const roster = getStudentsForAssignment(
        assignment,
        classBundle?.classes || [],
      );
      // Bulk upload is for Not Submitted students only
      setTaskStudents(excludeStudentsWithSubmissions(roster, submissions));
    });

    return () => {
      cancelled = true;
    };
  }, [bulkUploadTaskId]);

  const setSelectedValue = (type, selectValue) => {
    if (type === 'classes') {
      if (!selectValue) {
        setSelectedClass('');
      } else {
        // Find the class ID from the label
        const clazz = classes.find((c) => c.title === selectValue);
        setSelectedClass(clazz ? clazz.id : '');
      }
    }
  };

  const filteredData = (tasks) => {
    const sortedTasks = tasks.sort((a, b) => {
      const dateA = new Date(a.dueAt).getTime();
      const dateB = new Date(b.dueAt).getTime();
      return sortItem ? dateB - dateA : dateA - dateB;
    });
    return sortedTasks;
  };

  const sortedTasks = filteredData(filteredTasks);
  const tasksWithClassTitles = (classList) =>
    sortedTasks.map((task) => {
      const ids = task.classIds || (task.classId ? [task.classId] : []);
      const titles = ids
        .map((id) => (classList || []).find((c) => String(c.id) === String(id))?.title)
        .filter(Boolean);
      return { ...task, classTitle: titles.length ? titles.join(', ') : null };
    });
  const tasksWithTitles = tasksWithClassTitles(classData);
  const drafts = getDraftTasks(tasksWithTitles);
  const awaitingSubmissions = getAwaitingSubmissions(tasksWithTitles);
  const feedbacks = getInProgressTasks(tasksWithTitles);

  const classesItems = classes.map((clazz) => {
    return { value: clazz.id, label: clazz.title, category: 'CLASSES' };
  });

  const menuItems = [
    {
      name: 'TYPES',
      title: 'Types',
      items: [
        {
          value: 'TASK',
          label: 'Tasks',
          category: 'TYPES',
        },
        {
          value: 'DRAFT_REVIEW',
          label: 'Community',
          category: 'TYPES',
        },
      ],
    },
    {
      name: 'CLASSES',
      title: 'Classes',
      items: classesItems,
    },
  ];

  const filterTasks = (selectedItems) => {
    const groupedData = groupBy(selectedItems, 'category');
    let typesValues = (get(groupedData, 'TYPES') || []).map((item) => item.value);
    const filteredAssignments = assignmentsFilteredByClass.filter((assignment) => {
      if (!typesValues.length) return true;
      return typesValues.includes(assignment.type);
    });
    const classesValues = (get(groupedData, 'CLASSES') || []).map((item) => item.value);
    const filteredClasses = filteredAssignments.filter((assignment) => {
      if (!classesValues.length) return true;
      const classIds = assignment.classIds || (assignment.classId ? [assignment.classId] : []);
      return classIds.some((classId) => classesValues.includes(classId));
    });
    setFilteredTasks(filteredClasses);
  };

  const hidedeletePopup = () => {
    setShowDeletePopup(false);
  };
  const showDeletePopuphandler = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDeletePopup(true);
  };

  const showClosePopupHandler = (taskId) => {
    setCloseTask({ popup: true, id: taskId });
  };

  const showDateExtendPopuphandler = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDateExtendPopup(true);
  };
  const hideDateExtendPopup = () => {
    setShowDateExtendPopup(false);
  };

  const showSchedulePublishPopupHandler = (assignment) => {
    setSelectedAssignment(assignment);
    setShowSchedulePublishPopup(true);
  };

  const hideSchedulePublishPopup = () => {
    setShowSchedulePublishPopup(false);
  };

  const assignmentIdForDuplicate = async (id) => {
    try {
      const duplicatedTask = await duplicateAssignment(id, assignments);
      const duplicatedTaskId = duplicatedTask?.id;

      if (!duplicatedTaskId) {
        throw new Error('Duplicated task id was not returned');
      }

      queryClient.setQueryData(['assignments'], (currentAssignments) => {
        if (!Array.isArray(currentAssignments)) {
          return currentAssignments;
        }

        const isAlreadyPresent = currentAssignments.some(
          (assignment) =>
            String(assignment.id) === String(duplicatedTask.id),
        );

        if (isAlreadyPresent) {
          return currentAssignments;
        }

        return [duplicatedTask, ...currentAssignments];
      });
      queryClient.invalidateQueries({
        queryKey: ['assignments'],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['tasks'],
        refetchType: 'active',
      });

      window.location.href = `#/tasks/${duplicatedTaskId}`;
    } catch (error) {
      toast(<Toast message={'Failed to duplicate Task. Please try again.'} />);
    }
  };
  const showAssignmentStatusPopupHandler = (assignment) => {
    setSelectedAssignmentForStatus(assignment);
    setShowAssignmentStatusPopup(true);
  };

  const hideAssignmentStatusPopup = () => {
    setShowAssignmentStatusPopup(false);
  };

  const handleUploadResponses = bulkUploadGate.guard(async (task) => {
    if (!task?.id) {
      toast(<Toast message="Task not found. Please try again." />);
      return;
    }

    // Open bulk upload modal for tasks
    setBulkUploadTaskId(task.id);
  });

  const handleBulkUploadExit = () => {
    queryClient.invalidateQueries(['assignments']);
    queryClient.invalidateQueries(['tasks']);
    setBulkUploadTaskId(null);
  };

  const handleBulkUploadComplete = async (response) => {
    const assignmentId = response?.assignmentId || bulkUploadTaskId;

    const pollAssignmentUpdate = async () => {
      const maxAttempts = 30;
      const pollInterval = 1000;
      let lastAssignment = null;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const assignment = await getAssignmentById(assignmentId).catch(() => null);
        lastAssignment = assignment || lastAssignment;

        if (assignment && assignment.submissionCount > 0) {
          return assignment;
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }

      console.warn(
        `[Bulk Upload] Polling timeout: submissionCount not updated after ${maxAttempts} seconds for assignment ${assignmentId}`,
        lastAssignment
      );
      return lastAssignment;
    };

    if (assignmentId) {
      await pollAssignmentUpdate();
    }

    // Invalidate and refetch all relevant queries
    await Promise.all([
      queryClient.invalidateQueries(['assignments']),
      queryClient.invalidateQueries(['tasks']),
      queryClient.invalidateQueries(['submissions']),
      queryClient.invalidateQueries(['allDocuments']),
    ]);

    await Promise.all([
      queryClient.refetchQueries(['assignments'], { active: true }),
      queryClient.refetchQueries(['tasks'], { active: true }),
      queryClient.refetchQueries(['allDocuments'], { active: true }),
      queryClient.refetchQueries(['submissions'], { active: true }),
    ]);

    invalidateBulkUploadQueries(queryClient).catch(() => {});

    setBulkUploadTaskId(null);

    // Check if this task should navigate to feedback screen
    // Only navigate for JEDDAI and JEDDAI_ASSISTED tasks
    if (assignmentId) {
      const assignment = await getAssignmentById(assignmentId).catch(() => null);
      const reviewedBy = assignment?.reviewedBy;

      // Only navigate to feedback screen for jeddai-assisted tasks
      const shouldNavigateToFeedback = reviewedBy === 'JEDDAI' || reviewedBy === 'JEDDAI_ASSISTED';

      if (shouldNavigateToFeedback) {
        const path = openedSubmissionPath(response);
        if (path) {
          window.location.href = `#${path}`;
        }
      }
      // For non-jeddai tasks (TEACHER, P2P), just close the dialog without navigation
    } else {
      // Fallback: if no assignmentId, try to navigate anyway (legacy behavior)
      const path = openedSubmissionPath(response);
      if (path) {
        window.location.href = `#${path}`;
      }
    }
  };

  const onCloseTask = async (id) => {
    try {
      await closeAssignment(id);

      toast(<Toast message={'Task closed'} />);

      queryClient.invalidateQueries(['assignments']);
      // queryClient.invalidateQueries(['submissionsById']);
      queryClient.removeQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'submissionById',
      });
    } catch (error) {
      console.error('Error closing task:', error);
      toast(<Toast message={'Failed to close assignment'} />);
    } finally {
      setCloseTask({ popup: false, id: null });
    }
  };


  const hideClosePopupHandler = () => {
    setCloseTask((prev) => ({ ...prev, popup: false }));
  }
  
  const validateScheduleTime = (selectedDate) => {
    if (!dayjs(selectedDate).isValid()) {
      toast.error('Invalid publish time selected');
      return false;
    }

    const dueDate = new Date(selectedAssignment.dueAt);
    const publishDate = new Date(selectedDate);

    if (isDueDateNotSet(selectedAssignment.dueAt)) {
      toast(<Toast message={'Please set a due time before scheduling'} />);
      return false;
    }

    if (!isDueDateOneHourAfterPublish(publishDate, dueDate)) {
      toast(
        <Toast
          message={'Due time must be at least one hour after publish time'}
        />
      );

      const publishTimeContainer = document.getElementById(
        'publishTimeContainer'
      );
      if (publishTimeContainer) {
        publishTimeContainer.style.border = '1px solid red';
      }

      return false;
    }

    if (publishDate <= new Date()) {
      const publishTimeContainer = document.getElementById(
        'publishTimeContainer'
      );
      if (publishTimeContainer) {
        publishTimeContainer.style.border = '1px solid red';
      }

      toast(<Toast message={'Please choose a future publish time'} />);
      return false;
    }

    return true;
  };

  // Validate due time
  const validateDueTime = (selectedDate) => {
    if (!dayjs(selectedDate).isValid()) {
      toast.error('Invalid due date selected');
      return false;
    }

    const dueDate = new Date(selectedDate);
    const publishDate = new Date();

    if (publishDate && !isDueDateOneHourAfterPublish(publishDate, dueDate)) {
      const dueDateContainer = document.getElementById('dueTimeContainer');
      if (dueDateContainer) {
        dueDateContainer.style.border = '1px solid red';
      }

      toast(
        <Toast
          message={'Due time must be at least one hour from now'}
        />
      );

      return false;
    }

    return true;
  };

  const FilterSortAndCal = (
    <>
      <MainContainer>
        <CalenderContainer>
          {tabletView && <MenuButton setShowMenu={setShowMenu} />}
          <TitleHeading
            style={tasksSelected ? { color: '#7200E0' } : { color: '#7B7382' }}
            className={tasksSelected ? 'active' : ''}
            onClick={() => setTasksSelected(true)}
          >
            <TasksImg
              src={!tasksSelected ? TaskNotSelected : TaskSelected}
              selected={tasksSelected}
            />
            Column
          </TitleHeading>

          <TitleHeading
            style={tasksSelected ? { color: '#7B7382' } : { color: '#7200E0' }}
            className={!tasksSelected ? 'active' : ''}
            onClick={() => setTasksSelected(false)}
          >
            <TasksImgCal
              src={!tasksSelected ? CalNotSelected : CalSelected}
              selected={!tasksSelected}
            />
            Calendar
          </TitleHeading>
        </CalenderContainer>
        <FilterAndSortContainer>
          <FilterContainer>
            <FilterLabelContainer>
              <FilterIcon src={FilterSquare} alt="Filter" />
              <FilterLabel>Filters:</FilterLabel>
            </FilterLabelContainer>
            <RoundedDropDown
              search={false}
              type={'classes'}
              selectedIndex={setSelectedValue}
              menuItems={classesItems.map((c) => c.label)}
              defaultValue={classesItems.find((c) => c.value === selectedClass)?.label || ''}
              emptyLabel="All Classes"
              width={110}
            />
          </FilterContainer>
          {!tabletView && <FilterLine />}
          {tasksSelected && (
            <SortItems
              sortItem={sortItem}
              setSortItem={setSortItem}
              firstSortText={'New to Old'}
              secondSortText={'Old to New'}
            />
          )}
        </FilterAndSortContainer>
      </MainContainer>
    </>
  );

  function classForCalender(classId) {
    if (!Array.isArray(classId)) {
      return '';
    }

    const filteredClasses = classes.filter((classItem) =>
      classId.includes(classItem.id)
    );
    const classTitles = filteredClasses.map(
      (filteredClass) => filteredClass.title
    );

    return classTitles;
  }


  const tasksForCalendar = [
    ...drafts,
    ...awaitingSubmissions,
    ...feedbacks
  ];
  
  // Filter out tasks with no due date — react-big-calendar would otherwise place
  // them at epoch (Jan 1 1970) or render Invalid Date. Undated drafts/assignments
  // are explicitly supported elsewhere via isDueDateNotSet, so they belong in the
  // list view but not the calendar view.
  const calenderEvents = tasksForCalendar
    .filter((task) => task.dueAt)
    .map((task) => ({
      link: task.link,
      title: task.title,
      class: classForCalender(task.classIds),
      start: new Date(task.dueAt),
      end: new Date(task.dueAt),
    }));
  
  const MyCalendarFile = (
    <Suspense fallback={<TaskListSkeleton items={3} />}>
      <MyCalendar calenderEvents={calenderEvents} />
    </Suspense>
  );
  if (
    assignmentsQuery.isLoading ||
    teacherClassesQuery.isLoading ||
    documentReviewTasksQuery.isLoading ||
    isLoadingclassData
  ) {
    return <TaskListSkeleton />;
  }
  return (
    <>
      {closeTask.popup && (
        <GeneralPopup
          hidePopup={hideClosePopupHandler}
          title="Close task"
          textContent={
          <>Are you sure you want to close this task?</>
          }
          buttonText="Close"
          confirmButtonAction={() => onCloseTask(closeTask.id)}
          warningMessage={<> Closing this task will prevent students from submitting their work
            and will also lock feedback on all current submissions. If any
            students are currently working on this task, they may lose their
            progress.</>}
          closeBtnText="Cancel"
          cancelPopup={hideClosePopupHandler}
        />
      )}
      {showDeletePopup && (
        <DeleteAssignmentPopup
          assignment={selectedAssignment}
          hidedeletePopup={hidedeletePopup}
        />
      )}

      {showAssignmentStatusPopup && selectedAssignmentForStatus && (
        <RubricOutcomesPopup
          assignmentId={selectedAssignmentForStatus.id}
          hideAssignmentStatusPopup={hideAssignmentStatusPopup}
        />
      )}

      {showDateExtendPopup && (
        <DatePickerPopup
          isOpen={showDateExtendPopup}
          onClose={hideDateExtendPopup}
          onSubmit={(selectedDate) => {
            extendDueAtAssignment(selectedAssignment.id, {
              dueAt: selectedDate.toISOString(),
            })
              .then((res) => {
                queryClient.invalidateQueries(['notifications']);
                queryClient.invalidateQueries(['assignments']);

                window.location.href = '#tasks';
                window.location.reload();
                toast(<Toast message="Task due time changed" />);
              })
              .catch((error) => {
                toast.error('Failed to extend Task');
              });
          }}
          initialDate={selectedAssignment?.dueAt}
          title="Change due time"
          description={`Set new due time for ${selectedAssignment?.title}`}
          dateType="due"
          containerElementId="dueTimeContainer"
          validateDate={validateDueTime}
          submitButtonText="Change"
        />
      )}

      {showSchedulePublishPopup && (
        <DatePickerPopup
          isOpen={showSchedulePublishPopup}
          onClose={hideSchedulePublishPopup}
          onSubmit={(selectedDate) => {
            schedulepublishAssignment(selectedAssignment.id, {
              publishScheduledAt: selectedDate.toISOString(),
            })
              .then((res) => {
                queryClient.invalidateQueries(['notifications']);
                queryClient.invalidateQueries(['assignments']);
                queryClient.invalidateQueries((queryKey) =>
                  queryKey.includes('class')
                );

                window.location.href = '#tasks';
                window.location.reload();
                toast(
                  <Toast
                    message={`Task is scheduled for ${selectedDate.format(
                      'MMMM D, YYYY'
                    )}`}
                  />
                );
              })
              .catch((error) => {
                toast.error('Failed to schedule Task');
              });
          }}
          initialDate={
            selectedAssignment?.publishScheduledAt || dayjs().add(1, 'day')
          }
          minDate={dayjs()}
          title="Change publish time"
          description={`Set new publish time for ${selectedAssignment?.title}`}
          dateType="scheduled"
          containerElementId="publishTimeContainer"
          validateDate={validateScheduleTime}
          submitButtonText="Schedule"
        />
      )}

      <TeacherTasksDesktop
        {...{
          menuItems,
          filterTasks,
          drafts,
          awaitingSubmissions,
          feedbacks,
          showDeletePopuphandler,
          showClosePopupHandler,
          showDateExtendPopuphandler,
          showSchedulePublishPopupHandler,
          showAssignmentStatusPopupHandler,
          onUploadResponses: handleUploadResponses,
          FilterSortAndCal,
          tasksSelected,
          MyCalendarFile,
          isShowMenu,
          setShowMenu,
          ...tasksDesktopData(classData),
          assignmentIdForDuplicate,
          classData,
        }}
      />

      {bulkUploadGate.popup}
      {bulkUploadTaskId && (
        <BulkUploadDialog
          open={Boolean(bulkUploadTaskId)}
          onClose={handleBulkUploadExit}
        >
          <BulkUploadFullPage
            frame="dialog"
            entry="tasks"
            assignmentId={bulkUploadTaskId}
            students={taskStudents}
            onExit={handleBulkUploadExit}
            onComplete={handleBulkUploadComplete}
          />
        </BulkUploadDialog>
      )}
    </>
  );
}

const frame13041Data = {
  iconsaxLinearSort: '/img/iconsax-linear-sort@2x.png',
};
const tabs23Data = {
  children: 'In progress',
  className: 'tabs-3',
};
const tabs24Data = {
  children: 'Overdue',
  className: 'tabs-4',
};
const statusBubbles28Data = {
  children: 'Theory',
};
const statusBubbles32Data = {
  star1: '/img/star1@2x.png',
};

const frame66Data = {
  statusBubbles2Props: statusBubbles28Data,
  statusBubbles3Props: statusBubbles32Data,
};

const content6Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards6Data = {
  frame6Props: frame66Data,
  contentProps: content6Data,
};

const statusBubbles29Data = {
  children: 'Assignment',
};

const statusBubbles210Data = {
  children: 'MCQ',
};

const frame622Data = {
  statusBubbles21Props: statusBubbles29Data,
  statusBubbles22Props: statusBubbles210Data,
};

const content7Data = {
  dueOn2April2023: 'Due on 10 April 2023',
};

const cards22Data = {
  frame62Props: frame622Data,
  contentProps: content7Data,
};

const statusBubbles211Data = {
  children: 'MCQ',
};

const frame632Data = {
  statusBubbles2Props: statusBubbles211Data,
};

const content8Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards32Data = {
  frame63Props: frame632Data,
  contentProps: content8Data,
};

const statusBubbles212Data = {
  children: 'Theory',
};

const frame642Data = {
  statusBubbles2Props: statusBubbles212Data,
};

const content9Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards42Data = {
  frame64Props: frame642Data,
  contentProps: content9Data,
};

const statusBubbles213Data = {
  children: 'Assignment',
};

const statusBubbles214Data = {
  children: 'Theory',
};

const frame652Data = {
  statusBubbles21Props: statusBubbles213Data,
  statusBubbles22Props: statusBubbles214Data,
};

const content10Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards52Data = {
  frame65Props: frame652Data,
  contentProps: content10Data,
};

const frame192Data = {
  className: 'frame-19-1',
  cardsProps: cards6Data,
  cards2Props: cards22Data,
  cards3Props: cards32Data,
  cards4Props: cards42Data,
  cards5Props: cards52Data,
};

const statusBubbles216Data = {
  children: 'Assignment',
};

const statusBubbles217Data = {
  children: 'MCQ',
};

const frame623Data = {
  statusBubbles21Props: statusBubbles216Data,
  statusBubbles22Props: statusBubbles217Data,
};

const content23Data = {
  dueOn2April2023: 'Due on 10 April 2023',
};

const cards7Data = {
  frame62Props: frame623Data,
  content2Props: content23Data,
};

const statusBubbles218Data = {
  children: 'MCQ',
};

const frame633Data = {
  statusBubbles2Props: statusBubbles218Data,
};

const content24Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards8Data = {
  frame63Props: frame633Data,
  content2Props: content24Data,
};

const statusBubbles219Data = {
  children: 'Theory',
};

const frame643Data = {
  statusBubbles2Props: statusBubbles219Data,
};

const content25Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards9Data = {
  frame64Props: frame643Data,
  content2Props: content25Data,
};

const statusBubbles220Data = {
  children: 'Assignment',
};

const statusBubbles221Data = {
  children: 'Theory',
};

const frame653Data = {
  statusBubbles21Props: statusBubbles220Data,
  statusBubbles22Props: statusBubbles221Data,
};

const content26Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards10Data = {
  frame65Props: frame653Data,
  content2Props: content26Data,
};
const notifications3Data = {
  src: '/img/notificationbing@2x.png',
};

const statusBubbles21Data = {
  children: 'Theory',
};

const statusBubbles31Data = {
  star1: '/img/star1@2x.png',
};
const frame61Data = {
  statusBubbles2Props: statusBubbles21Data,
  statusBubbles3Props: statusBubbles31Data,
};

const content1Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards1Data = {
  frame6Props: frame61Data,
  contentProps: content1Data,
};

const statusBubbles22Data = {
  children: 'Assignment',
};

const statusBubbles23Data = {
  children: 'MCQ',
};

const frame621Data = {
  statusBubbles21Props: statusBubbles22Data,
  statusBubbles22Props: statusBubbles23Data,
};

const content2Data = {
  dueOn2April2023: 'Due on 10 April 2023',
};

const cards21Data = {
  frame62Props: frame621Data,
  contentProps: content2Data,
};

const statusBubbles24Data = {
  children: 'MCQ',
};

const frame631Data = {
  statusBubbles2Props: statusBubbles24Data,
};

const content3Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards31Data = {
  frame63Props: frame631Data,
  contentProps: content3Data,
};

const statusBubbles25Data = {
  children: 'Theory',
};

const frame641Data = {
  statusBubbles2Props: statusBubbles25Data,
};

const content4Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards41Data = {
  frame64Props: frame641Data,
  contentProps: content4Data,
};

const statusBubbles26Data = {
  children: 'Assignment',
};

const statusBubbles27Data = {
  children: 'Theory',
};

const frame651Data = {
  statusBubbles21Props: statusBubbles26Data,
  statusBubbles22Props: statusBubbles27Data,
};

const content5Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards51Data = {
  frame65Props: frame651Data,
  contentProps: content5Data,
};
const frame191Data = {
  cardsProps: cards1Data,
  cards2Props: cards21Data,
  cards3Props: cards31Data,
  cards4Props: cards41Data,
  cards5Props: cards51Data,
};

const frame13043Data = {
  iconsaxLinearSort: '/img/iconsax-linear-sort-2@2x.png',
};
const frame13061Data = {
  frame1304Props: frame13043Data,
};
const tasksDesktopData = (classData) => {
  return {
    title: 'Tasks',
    frame1306Props: frame13061Data,
    frame19Props: frame192Data,
    headerProps: taskHeaderProps(classData),
  };
};

const FilterLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterIcon = styled.img`
  width: 26px;
  height: 26px;
`;

const FilterLabel = styled.span`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #7B7382;
`;
